import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUi } from "@/context/UiContext";

type Tip = {
  key: string;
  label: string;
  question: string;
};

const ALL_TIPS: Tip[] = [
  { key: "direction", label: "想找方向？先聊聊", question: "我最近不太確定自己的方向，想先聊聊。" },
  { key: "corporate", label: "企業培訓？先聊聊", question: "我想詢問企業培訓，可以先聊聊。" },
  { key: "delay", label: "一直拖延？先聊聊", question: "我最近一直拖延，想了解可以怎麼開始調整。" },
  { key: "consistency", label: "三分鐘熱度？先聊聊", question: "我做事常三分鐘熱度，想了解可以怎麼改善。" },
  { key: "courses", label: "想了解課程？先聊聊", question: "我想了解適合我的課程。" },
  { key: "system", label: "想了解幸福人才系統？", question: "我想了解幸福人才養成系統是什麼。" },
  { key: "person", label: "許文彰是誰？", question: "我想了解許文彰是誰、他的專業背景。" },
  { key: "cooperate", label: "想邀約演講／合作？", question: "我想詢問講師邀約或商業合作。" },
];

/** 依頁面決定優先顯示的快捷句（越前面越優先）。 */
const PAGE_PRIORITY: Record<string, string[]> = {
  "/": ["direction", "system", "courses", "corporate"],
  "/about": ["person", "system", "cooperate"],
  "/happiness-talent-system": ["system", "direction", "courses"],
  "/personal-growth": ["delay", "direction", "consistency", "courses"],
  "/corporate-training": ["corporate", "cooperate", "system"],
  "/courses": ["courses", "system", "direction"],
  "/ai-coach": ["direction", "system", "courses"],
  "/contact": ["cooperate", "corporate", "courses"],
};

function buildTips(pathname: string): Tip[] {
  const priority = PAGE_PRIORITY[pathname] ?? PAGE_PRIORITY["/"];
  const ordered: Tip[] = [];
  priority.forEach((key) => {
    const tip = ALL_TIPS.find((item) => item.key === key);
    if (tip && !ordered.includes(tip)) ordered.push(tip);
  });
  ALL_TIPS.forEach((tip) => {
    if (!ordered.includes(tip)) ordered.push(tip);
  });
  return ordered;
}

export default function AiHintCard() {
  const { askAi } = useUi();
  const { pathname } = useLocation();
  const tips = useMemo(() => buildTips(pathname), [pathname]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [pathname]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % tips.length);
    }, 2800);
    return () => window.clearInterval(timer);
  }, [tips.length]);

  const tip = tips[index % tips.length];

  return (
    <div
      className="fixed right-4 md:right-6 z-40 pointer-events-none"
      style={{ bottom: "calc(96px + env(safe-area-inset-bottom))" }}
    >
      <div className="relative bg-primary-dark text-cream rounded-card px-4 py-3 w-[210px] md:w-[236px] border border-gold/30">
        <p className="text-sm md:text-[15px] font-semibold leading-snug whitespace-nowrap text-cream">
          AI線上客服
        </p>
        <button
          type="button"
          onClick={() => askAi(tip.question)}
          className="group mt-1 flex w-full items-center justify-between gap-1 text-left text-[11px] md:text-xs text-gold-soft leading-snug pointer-events-auto cursor-pointer hover:text-gold transition-colors"
          title="點擊直接詢問 AI 幸福人才顧問"
        >
          <span key={`${pathname}-${index}`} className="ai-hint-fade whitespace-nowrap">
            {tip.label}
          </span>
          <span className="flex items-center gap-0.5 whitespace-nowrap text-gold/90 transition-transform group-hover:translate-x-0.5">
            立即詢問
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
          </span>
        </button>
        <span className="absolute -bottom-[7px] right-5 w-3 h-3 bg-primary-dark border-b border-r border-gold/30 rotate-45" />
      </div>
    </div>
  );
}